import { SocialNetworksId } from "@/interfaces";
import { ShieldCheck, UserPlus, Link as LinkIcon } from "lucide-react";

export type SocialProfileState = "NOT_LINKED" | "CONNECTING" | "SYNCING" | "CONNECTED" | "DISCONNECTING";

interface SocialProfileStatusStyle {
  bg: string;
  text: string;
  color: string;
}

export const isTwoStepNetwork = (networkId: SocialNetworksId) => ["FB", "IG", "LI", "GL"].includes(networkId);

export const isReviewNetwork = (networkId: SocialNetworksId) => ["GL", "ZL", "FB"].includes(networkId);

export const requiresConfigAfterConnection = (networkId: SocialNetworksId) => ["TT"].includes(networkId);

export const getStatusStyle = (profileState?: SocialProfileState): SocialProfileStatusStyle | undefined => {
  switch (profileState) {
    case "DISCONNECTING":
      return { bg: "bg-red-500 animate-pulse", text: "DISCONNECTING", color: "text-red-500" };
    case "CONNECTING":
      return { bg: "bg-blue-500 animate-pulse", text: "CONNECTING", color: "text-blue-500" };
    case "SYNCING":
      return { bg: "bg-emerald-500 animate-pulse", text: "CONNECTED", color: "text-emerald-600" };
    case "CONNECTED":
      return { bg: "bg-emerald-500", text: "CONNECTED", color: "text-emerald-600" };
    case "NOT_LINKED":
      return { bg: "bg-red-400", text: "NOT LINKED", color: "text-slate-400" };
    case undefined:
      return undefined;
  }
};

export const helpGuideCopy: Partial<Record<SocialNetworksId, any>> = {
  FB: {
    title: "Having problems connecting your Business Page?",
    description: "Ensure your Facebook setup is ready to go:",
    steps: [
      {
        icon: UserPlus,
        title: "Need a Business Page?",
        description: "You must have a dedicated business page to post.",
        linkText: "Create one here",
        url: "https://www.facebook.com/business/help/1199464373557428",
        videoText: "Video Guide",
        videoUrl: "https://myoc.io/v/g5Ll8fti",
      },
      {
        icon: ShieldCheck,
        title: "Check Page Access",
        description: "Make sure your personal account has full admin access.",
        linkText: "Learn how to check",
        url: "https://www.facebook.com/business/help/2169003770027706",
      },
    ],
  },
  IG: {
    title: "Having problems connecting your account?",
    description: "Instagram requires a specific setup for third-party publishing:",
    steps: [
      {
        icon: UserPlus,
        title: "Create an Account",
        description: "Don't have an Instagram account yet?",
        linkText: "Create one here",
        url: "https://help.instagram.com/155940534568753",
      },
      {
        icon: ShieldCheck,
        title: "Switch to Professional",
        description: "Your account must be set to 'Professional' or 'Business'.",
        linkText: "See how to switch",
        url: "https://help.instagram.com/502981923235522",
      },
      {
        icon: LinkIcon,
        title: "Connect to Facebook",
        description: "Your IG must be linked to a Facebook Business Page.",
        linkText: "Guide to linking",
        url: "https://www.facebook.com/business/help/620548115562686",
        videoText: "Video Guide",
        videoUrl: "https://myoc.io/v/feXzGDaR",
      },
    ],
  },
  LI: {
    title: "Having problems connecting your LinkedIn?",
    description: "Make sure your LinkedIn profile or Company Page is ready:",
    steps: [
      {
        icon: UserPlus,
        title: "Need a Company Page?",
        description: "You can connect a personal profile or a dedicated Company Page.",
        linkText: "Learn about pages",
        url: "https://business.linkedin.com/advertise/cx/21/11/linkedin-pages",
      },
      {
        icon: ShieldCheck,
        title: "Check Admin Rights",
        description: "If connecting a Company Page, ensure you are an Admin.",
        linkText: "View admin roles",
        url: "https://www.linkedin.com/help/linkedin/answer/a8024204",
      },
    ],
  },
  TT: {
    title: "Having problems connecting TikTok?",
    description: "Set up your TikTok account for third-party publishing:",
    steps: [
      {
        icon: UserPlus,
        title: "Create an Account",
        description: "Don't have a TikTok account yet?",
        linkText: "Sign up here",
        url: "https://support.tiktok.com/en/getting-started",
      },
      {
        icon: ShieldCheck,
        title: "Switch to Business Account",
        description: "Unlock business features by switching your account type.",
        linkText: "How to switch",
        url: "https://getstarted.tiktok.com/Business-accounts",
      },
    ],
  },
  TW: {
    title: "Having problems connecting your X (Twitter) account?",
    description: "Ensure your account is active and ready to authorize:",
    steps: [
      {
        icon: UserPlus,
        title: "Create an Account",
        description: "You need an active X (Twitter) account to connect.",
        linkText: "Sign up here",
        url: "https://help.x.com/en/using-x/create-x-account",
      },
      {
        icon: ShieldCheck,
        title: "App Permissions",
        description: "Make sure you approve the authorization prompt when connecting.",
        linkText: "Learn about permissions",
        url: "https://help.x.com/en/managing-your-account/connect-or-revoke-access-to-third-party-apps",
      },
    ],
  },
  GL: {
    title: "Having problems connecting Google Business Profile?",
    description: "Ensure your Business Profile exists and the Google account you use can manage it:",
    steps: [
      {
        icon: UserPlus,
        title: "Add or claim your profile",
        description: "You need an active Google Business Profile for your business before you can connect.",
        linkText: "How to add or claim",
        url: "https://support.google.com/business/answer/2911778",
      },
      {
        icon: ShieldCheck,
        title: "Owner or manager access",
        description:
          "Sign in with a Google account that owns or manages the Business Profile, then approve access when prompted.",
        linkText: "Manage owners and managers",
        url: "https://support.google.com/business/answer/3403100",
      },
    ],
  },
  ZL: {
    title: "Having problems connecting your Zillow profile?",
    description: "Use the public URL from your Zillow agent profile page:",
    steps: [
      {
        icon: LinkIcon,
        title: "Copy your profile URL",
        description: "Open your Zillow agent profile in a browser and copy the full URL from the address bar.",
        linkText: "Go to Zillow",
        url: "https://www.zillow.com/",
      },
      {
        icon: ShieldCheck,
        title: "Use the exact profile link",
        description: "The URL should look like zillow.com/profile/your-name. Paste that link when connecting.",
        linkText: "Zillow profile help",
        url: "https://www.zillow.com/agent-resources/",
      },
    ],
  },
};

export type ZillowHelpVariant = "agent" | "lender";

export const zillowHelpGuideCopy: Record<ZillowHelpVariant, (typeof helpGuideCopy)["ZL"]> = {
  agent: helpGuideCopy.ZL!,
  lender: {
    title: "Having problems connecting your Zillow lender profile?",
    description: "Zillow must already have your NMLS ID on file before we can import your reviews:",
    steps: [
      {
        icon: ShieldCheck,
        title: "Double-check your NMLS ID",
        description:
          "Confirm the NMLS ID matches your Zillow lender profile. If you represent a company that shares an NMLS ID, try adding the company name.",
        linkText: "Go to Zillow",
        url: "https://www.zillow.com/",
      },
      {
        icon: LinkIcon,
        title: "Claim your profile in Lender Hub",
        description:
          "If Zillow doesn't have your NMLS number in their system yet, sign in to Zillow Lender Hub to claim or create your lender profile, then try connecting again.",
        linkText: "Open Zillow Lender Hub",
        url: "https://www.zillow.com/mortgage/lender-hub/",
      },
    ],
  },
};

export const unlinkedDescriptions: Partial<Record<SocialNetworksId, string>> = {
  IG_STORY: "",
  SC: "",
  FB: "Connect your Facebook business page to easily schedule your first posts and start building your brand presence.",
  IG: "Link your Instagram professional account to unlock auto-publishing and see how your content performs.",
  LI: "Connect your LinkedIn profile to start sharing your expertise and growing your professional network.",
  TT: "Connect your TikTok account to seamlessly publish your videos and start reaching a broader audience.",
  TW: "Link your X account to start sharing quick updates and engaging with real-time conversations in your industry.",
  GL: "Connect your Google Business Profile to manage your local presence and keep customers up to date on Search and Maps.",
  ZL: "Connect your Zillow agent or lender profile to import reviews and ratings.",
};
